<template>
  <div>
    <template v-if="workOrderGraph">
      <WorkOrderGraphic
        :showDetail="showDetail"
        :graphic="graphic"
        :series="series"
        :seriesData="seriesData"
        :showSpinner="showSpinner"
        :workOrderGraph="workOrderGraph"
        :selectedGraphIds="selectedGraphIds"
        @handleOpenChildren="handleOpenChildren"
        @updatedOpenedGraphs="updatedOpenedGraphs"
        @fetchSeries="fetchSeries"
        @onSelect="onSelect"
      />
    </template>
    <template v-else>
      <Card
        :title="getTitle"
        :flatCard="true"
        leftBorder
        leftIcon
        :isDense="false"
        colorToolBar="#fff"
      >
        <template #icon>
          <v-btn
            icon
            @click="handleOpenChildren(graphic.id)"
          >
            <v-icon
              large
              color="#26384B"
            >
              {{ iconArrow }}
            </v-icon>
          </v-btn>
        </template>
        <template
          #items
          :slot-scope="graphicId"
        >
          <v-menu offsetY>
            <template #activator="{ on }">
              <div class="action-buttons">
                <v-btn
                  color="primary"
                  fab
                  xSmall
                  v-on="on"
                >
                  <v-icon color="#fff">
                    mdi-dots-vertical
                  </v-icon>
                </v-btn>
              </div>
            </template>
            <v-list>
              <ListItem
                icon="mdi-pencil"
                :title="$t('graphics.edit')"
                @click="showModalEditGraphic(graphicId)"
              />
              <ListItem
                icon="mdi-cached"
                color="#ef4135"
                titleColor="#ef4135"
                :title="$t('graphics.reset')"
                @click="showModalResetAllGraphic(graphicId)"
              />
              <ListItem
                icon="mdi-delete"
                color="#ef4135"
                titleColor="#ef4135"
                :title="$t('graphics.delete')"
                @click="showModalDeleteGraphic(graphicId)"
              />
            </v-list>
          </v-menu>
        </template>
        <template #content>
          <GraphicContents
            :showDetail="showDetail"
            :graphic="graphic"
            :series="series"
            :seriesData="seriesData"
            :showSpinner="showSpinner"
            :workOrderGraph="false"
            @fetchSeries="fetchSeries"
            @handleOpenChildren="handleOpenChildren"
            @getSeriesData="fetchSeriesData"
          />
        </template>
      </Card>
    </template>
  </div>
</template>
<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { clone, cloneDeep } from 'lodash';
import ListItem from '@/components/shared/widgets/ListItem.vue';
import Card from '@/components/card/Card.vue';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import { defaultColors } from '@/constants/graphics';
import GraphicForm from '@/components/forms/GraphicForm.vue';
import GraphicContents from '@/components/graphics/GraphicContents.vue';
import WorkOrderGraphic from '@/components/graphics/WorkOrderGraphic.vue';
import { getSeriesData, appendLegendToSVG } from '@/utilities/graphs';

export default {
    name: 'Graphic',
    components: {
        ListItem,
        Card,
        GraphicContents,
        WorkOrderGraphic,
    },
    props: {
        name: {
            type: String,
            default: null,
        },
        graphicId: {
            type: String,
            default: null,
        },
        graphic: {
            type: Object,
            default: () => { },
        },
        workOrderGraph: {
            type: Boolean,
            default: false,
        },
        selectedGraphIds: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            openChildren: false,
            showDetail: false,
            series: [],
            seriesData: [],
            seriesNormsChartOptions: null,
            addSeriesDisabled: false,
            showSpinner: false,
        };
    },
    computed: {
        ...mapGetters([
            'lang',
            'teamMainLang',
        ]),
        iconArrow() {
            return this.openChildren ? 'mdi-menu-down' : 'mdi-menu-right';
        },
        getTitle() {
            return this.graphic.name ? this.graphic.name : this.$t('graphics.noName');
        },
    },
    methods: {
        formatSeries(series) {
            const clonedSeries = clone(series);
            return clonedSeries.map((s, index) => ({
                id: s.id,
                graphicId: s.graphic,
                filter: s.filter,
                analysisType: s.analysisType.id,
                component: s.component?.id,
                componentName: s.component?.name,
                analysisName: s.analysisType.name,
                color: defaultColors[index],
            }));
        },
        async fetchSeries(graphicId = this.graphic.id) {
            const { siteId } = this.$route.params;
            const { data: { series } } = await this.$rq.getSeries(siteId, graphicId, this.lang);
            this.series = this.formatSeries(series);
            this.$store.dispatch('setSeries', {
                id: graphicId,
                series: series.map((s, i) => ({ ...s, color: defaultColors[i] })),
            });
        },
        async handleOpenChildren() {
            this.showSpinner = true;
            this.$emit('freezeAllGraphs', true);
            this.showDetail = !this.showDetail;
            if (this.showDetail) {
                await this.fetchSeries();
                await this.fetchSeriesData();
            }
            this.showSpinner = false;
            this.$emit('freezeAllGraphs', false);
            this.openChildren = !this.openChildren;
        },
        showModalDeleteGraphic(graphicId) {
            this.$root.$emit('showModal', {
                title: this.$t('graphics.deleteGraphic'),
                component: Vue.component('confirmGraphicDelete', ConfirmForm),
                payload: { msg: this.$t('graphics.deleteMsgConfirmation') },
                onConfirm: () => this.deleteGraphic(graphicId),
                isForm: false,
            });
        },
        showModalEditGraphic(graphicId) {
            this.$root.$emit('showModal', {
                title: this.$t('graphics.editGraphic'),
                component: Vue.component('GraphicForm', GraphicForm),
                showLangs: false,
                payload: { name: this.name, isEdition: true },
                onConfirm: requestPayload => this.editGraphic(graphicId, requestPayload),
            });
        },
        showModalResetAllGraphic(graphicId) {
            this.$root.$emit('showModal', {
                title: this.$t('graphics.resetGraphicSeries'),
                component: Vue.component('confirmGraphicDelete', ConfirmForm),
                isForm: false,
                payload: { msg: this.$t('graphics.resetAllMsgConfirmation') },
                onConfirm: () => this.resetAllGraphicSeries(graphicId),
            });
        },
        deleteGraphic(graphicId) {
            const { siteId } = this.$route.params;
            this.$store.dispatch('deleteGraphic', { siteId, graphicId });
            this.fetchSeries(graphicId);
        },
        editGraphic(graphicId, graphicName) {
            const { siteId } = this.$route.params;
            this.$store.dispatch('editGraphic', { siteId, graphicId, graphic: { name: graphicName } });
        },
        async resetAllGraphicSeries(graphicId) {
            const { siteId } = this.$route.params;
            await this.$store.dispatch('resetAllGraphicSeries', { siteId, graphicId });
            await this.fetchSeries(graphicId);
        },
        onSelect(data) {
            this.$emit('onSelect', data);
        },

        async fetchSeriesData() {
            const { siteId } = this.$route.params;
            this.seriesData = await getSeriesData(this.$rq, siteId, this.graphic.id, this.lang);
        },
        async updatedOpenedGraphs(data) {
            // Create a deep clone of the data object
            const clonedData = cloneDeep(data);

            // Create a proper clone of the SVG element to avoid modifying the original
            const originalSvg = data.graph;
            const clonedSvg = originalSvg.cloneNode(true);

            // Add Legend to the cloned SVG
            clonedData.graph = await appendLegendToSVG(this.$rq, clonedSvg, this.series, clonedData.graphName, this.lang, this.teamMainLang);

            // Emit the event with the modified clone
            this.$emit('updatedOpenedGraphs', clonedData);
        },
    },
};
</script>
<style scoped>
.btn-chart {
  text-transform: none;
  letter-spacing: normal;
  margin-right: 8px;
}
</style>
